<template>
  <div class="record-table-wrapper" data-testid="dns-record-table-wrapper">
    <div class="record-type-header">
      <span class="record-type-badge" data-testid="record-type-badge">{{ recordType }}</span>
      <a-button
        size="small"
        type="primary"
        @click="copyAllRecords"
        class="copy-all-btn"
        data-testid="copy-all-records-button"
      >
        <template #icon>
          <copy-outlined />
        </template>
        Copy All
      </a-button>
    </div>
    
    <a-table
      v-if="!isMobile"
      :columns="columns"
      :data-source="records"
      :pagination="false"
      size="small"
      bordered
      class="dns-record-table"
      data-testid="dns-record-table"
    >
      <template #bodyCell="{ column, text, record }">
        <div v-if="column.dataIndex === 'actions'" class="actions-cell">
          <a-button
            size="small"
            type="link"
            @click="copyRow(record)"
            title="Copy entire row"
          >
            <template #icon>
              <copy-outlined />
            </template>
            <span class="cnfltd-hide-mobile">Copy</span>
          </a-button>
        </div>
        <div v-else class="cell-with-copy" @click="copyCellValue(text)">
          <span class="cell-value">{{ text }}</span>
          <copy-outlined class="copy-icon" />
        </div>
      </template>
      
      <template #headerCell="{ column }">
        <div class="header-with-copy">
          <span>{{ column.title }}</span>
        </div>
      </template>
    </a-table>

    <!-- Mobile Card Layout -->
    <div v-else class="record-mobile-list" data-testid="dns-record-mobile-list">
      <div v-for="(record, index) in records" :key="index" class="record-mobile-card">
        <div 
          v-for="col in columns.filter(c => c.key !== 'actions')" 
          :key="col.key" 
          class="record-mobile-row"
          @click="copyCellValue(record[col.dataIndex])"
        >
          <span class="row-label">{{ col.title }}</span>
          <span class="row-value">
            {{ record[col.dataIndex] }}
            <copy-outlined class="copy-icon" />
          </span>
        </div>
        <div class="record-mobile-actions">
          <a-button size="small" type="primary" ghost @click="copyRow(record)" class="copy-row-btn">
            <template #icon><copy-outlined /></template>
            Copy Entire Row
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { CopyOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import type { DnsRecord } from '../types'

const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

interface Props {
  records: DnsRecord[]
  recordType: string
}

const props = defineProps<Props>()

const formatLabel = (key: string): string => {
  // Convert camelCase or snake_case to Title Case
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

// Get all unique keys from records
const getRecordKeys = (): string[] => {
  const keysSet = new Set<string>()
  props.records.forEach((record: DnsRecord) => {
    Object.keys(record).forEach(key => {
      if (record[key as keyof DnsRecord] !== undefined && record[key as keyof DnsRecord] !== null) {
        keysSet.add(key)
      }
    })
  })
  return Array.from(keysSet)
}

// Create columns dynamically based on record fields
const columns = computed(() => {
  const keys = getRecordKeys()
  let cols = keys.map(key => ({
    title: formatLabel(key),
    dataIndex: key,
    key: key,
    ellipsis: true,
    width: key === 'ttl' ? (isMobile.value ? 65 : 100) : undefined
  }))
  
  // Filter out 'Class' column on mobile
  if (isMobile.value) {
    cols = cols.filter(col => col.key.toLowerCase() !== 'class')
  }
  
  // Add actions column for row copy
  cols.push({
    title: isMobile.value ? '' : 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    width: isMobile.value ? 40 : 100,
    ellipsis: false
  })
  
  return cols
})

// Copy individual cell value
const copyCellValue = async (value: any) => {
  try {
    const textToCopy = String(value)
    await navigator.clipboard.writeText(textToCopy)
    message.success('Copied to clipboard')
  } catch (err) {
    console.error('Failed to copy:', err)
    message.error('Failed to copy to clipboard')
  }
}

// Copy entire row
const copyRow = async (record: DnsRecord) => {
  try {
    const textToCopy = Object.entries(record)
      .filter(([key, value]) => key !== 'key' && value !== undefined && value !== null)
      .map(([key, value]) => `${formatLabel(key)}: ${value}`)
      .join('\n')
    
    await navigator.clipboard.writeText(textToCopy)
    message.success('Row copied to clipboard')
  } catch (err) {
    console.error('Failed to copy row:', err)
    message.error('Failed to copy row')
  }
}

// Copy all records
const copyAllRecords = async () => {
  try {
    const allRecordsText = props.records.map((record: DnsRecord, index: number) => {
      const recordText = Object.entries(record)
        .filter(([key, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${formatLabel(key)}: ${value}`)
        .join('\n')
      return `${props.recordType} Record ${index + 1}:\n${recordText}`
    }).join('\n\n')
    
    await navigator.clipboard.writeText(allRecordsText)
    message.success(`Copied all ${props.records.length} ${props.recordType} record(s)`)
  } catch (err) {
    console.error('Failed to copy all records:', err)
    message.error('Failed to copy all records')
  }
}
</script>

<style scoped lang="scss">
.record-table-wrapper {
  .record-type-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f0f0;
    
    .record-type-badge {
      font-weight: 700;
      font-size: 16px;
      color: #1890ff;
      padding: 4px 12px;
      background: #e6f7ff;
      border-radius: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .copy-all-btn {
      font-weight: 600;
    }
  }
  
  :deep(.dns-record-table) {
    .ant-table {
      font-size: 13px;
    }
    
    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 700;
      font-size: 12px;
      color: #262626;
      padding: 12px 8px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      border-bottom: 2px solid #e8e8e8;
      
      .header-with-copy {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px;
      
      .cell-with-copy {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 4px 8px;
        margin: -4px -8px;
        border-radius: 4px;
        transition: all 0.2s;
        
        &:hover {
          background: #f0f7ff;
          
          .copy-icon {
            opacity: 1;
          }
        }
        
        &:active {
          background: #e6f4ff;
        }
        
        .cell-value {
          flex: 1;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
          font-size: 13px;
          font-weight: 500;
          word-break: break-all;
          color: #262626;
        }
        
        .copy-icon {
          flex-shrink: 0;
          opacity: 0;
          transition: opacity 0.2s;
          font-size: 12px;
          color: #1890ff;
        }
      }
      
      .actions-cell {
        text-align: center;
        
        .ant-btn-link {
          padding: 0 8px;
          height: 28px;
          font-size: 12px;
          font-weight: 600;
          
          .anticon {
            font-size: 14px;
          }
        }
      }
    }
  }

  /* Mobile refinements */
  @media (max-width: 768px) {
    .cnfltd-hide-mobile {
      display: none;
    }

    .record-mobile-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .record-mobile-card {
      background: #ffffff;
      border: 1px solid #f0f0f0;
      border-radius: 8px;
      padding: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .record-mobile-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 8px 0;
        border-bottom: 1px solid #f5f5f5;
        cursor: pointer;

        &:last-child {
          border-bottom: none;
        }

        .row-label {
          font-weight: 700;
          color: #8c8c8c;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          flex-shrink: 0;
          width: 80px;
        }

        .row-value {
          flex: 1;
          text-align: right;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
          font-size: 13px;
          font-weight: 500;
          color: #262626;
          word-break: break-all;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;

          .copy-icon {
            font-size: 10px;
            color: #1890ff;
            opacity: 0.6;
          }
        }
      }

      .record-mobile-actions {
        margin-top: 12px;
        display: flex;
        justify-content: center;

        .copy-row-btn {
          width: 100%;
          font-weight: 600;
          height: 36px;
        }
      }
    }
  }
}
</style>
